import { useMemo, useState } from 'preact/hooks';
import { useModel } from '../model.ts';
import { cardId } from '../../shared/types.ts';
import { language } from '../../shared/languages.ts';
import { State, formatInterval, type CardInfo } from '../srs.ts';
import { playNote, clipsFor } from '../audio.ts';
import { Reading } from '../Reading.tsx';

function status(c: CardInfo | undefined): { label: string; cls: string } {
  if (!c || c.fsrs.state === State.New) return { label: 'new', cls: 'new' };
  if (c.fsrs.state !== State.Review) return { label: 'learning', cls: 'learn' };
  const ms = c.fsrs.dueAt.getTime() - Date.now();
  return ms <= 0 ? { label: 'due', cls: 'due' } : { label: formatInterval(ms), cls: 'known' };
}

/** Strip tone digits and spaces so "neihou" finds "nei5 hou2". */
function plain(s: string): string {
  return s.toLowerCase().replace(/[1-6\s'’]/g, '');
}

export function Browse({
  deckId,
  setDeckId,
  onEdit,
}: {
  deckId: string | null;
  setDeckId: (id: string | null) => void;
  onEdit: (id: string | null) => void;
}) {
  const m = useModel();
  const [query, setQuery] = useState('');
  const [tag, setTag] = useState<string | null>(null);

  const decks = m.liveDecks();
  const inDeck = m.liveNotes().filter((n) => !deckId || n.deckId === deckId);
  const tags = useMemo(() => [...new Set(inDeck.flatMap((n) => n.tags))].sort(), [m.version, deckId]);
  const q = query.trim().toLowerCase();
  const pq = plain(query);
  const shown = inDeck
    .filter((n) => !tag || n.tags.includes(tag))
    .filter(
      (n) =>
        !q ||
        n.meaning.toLowerCase().includes(q) ||
        n.text.toLowerCase().includes(q) ||
        n.notes.toLowerCase().includes(q) ||
        (pq && plain(n.reading).includes(pq)),
    )
    .sort(
      (a, b) =>
        (m.decks.get(a.deckId)?.position ?? 0) - (m.decks.get(b.deckId)?.position ?? 0) ||
        a.position - b.position ||
        a.createdAt - b.createdAt,
    );

  return (
    <div class="browse">
      <div class="browse-head">
        <input
          type="search"
          placeholder="Search"
          value={query}
          onInput={(e) => setQuery(e.currentTarget.value)}
        />
        <button onClick={() => onEdit(null)} disabled={!decks.length}>
          + Add
        </button>
      </div>
      {decks.length > 1 && (
        <div class="chips">
          <button class={`chip ${deckId === null ? 'on' : ''}`} onClick={() => setDeckId(null)}>
            All decks
          </button>
          {decks.map((d) => (
            <button key={d.id} class={`chip ${deckId === d.id ? 'on' : ''}`} onClick={() => setDeckId(d.id)}>
              {d.name}
            </button>
          ))}
        </div>
      )}
      <div class="chips">
        <button class={`chip ${tag === null ? 'on' : ''}`} onClick={() => setTag(null)}>
          all ({inDeck.length})
        </button>
        {tags.map((t) => (
          <button key={t} class={`chip ${tag === t ? 'on' : ''}`} onClick={() => setTag(tag === t ? null : t)}>
            {t}
          </button>
        ))}
      </div>
      <ul class="note-list">
        {shown.map((n) => {
          const lang = language(m.deckOf(n)?.language ?? 'none');
          const ear = lang.voices.length > 0 || n.recordings.length > 0;
          const rec = status(m.cards.get(cardId(n.id, 'recognize')));
          const prod = status(m.cards.get(cardId(n.id, 'produce')));
          return (
            <li key={n.id}>
              {ear || clipsFor(n).length ? (
                <button class="play" aria-label="Play" onClick={() => void playNote(n, lang.speech)}>
                  ▶
                </button>
              ) : null}
              <div class="note-main" onClick={() => onEdit(n.id)}>
                <div class="meaning">{lang.voices.length ? n.meaning : n.text}</div>
                <div>
                  {lang.voices.length ? (
                    <>
                      <Reading text={n.reading || n.text} tones={lang.readingStyle === 'tones'} />
                      {m.settings.showText && n.reading && <span class="text small"> {n.text}</span>}
                    </>
                  ) : (
                    <span class="muted">{n.meaning}</span>
                  )}
                </div>
              </div>
              <div class="note-status" onClick={() => onEdit(n.id)}>
                <span class={`pill ${rec.cls}`} title="recognize card">
                  {ear ? 'hear' : 'recognize'}: {rec.label}
                </span>
                <span class={`pill ${prod.cls}`} title="produce card">
                  {ear ? 'say' : 'recall'}: {prod.label}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
      {!decks.length && <p class="center muted">No decks yet. Make one under Decks.</p>}
      {decks.length > 0 && !shown.length && <p class="center muted">Nothing matches.</p>}
    </div>
  );
}
